"use client";

import { useCallback } from "react";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Session storage key used to communicate a pending social OAuth return.
 * Set before redirecting to the OAuth provider; cleared after the event fires.
 */
export const SOCIAL_AUTH_PENDING_KEY = "social_auth_pending";

export type SocialAuthPending = {
  provider: string;
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function gtag(...args: unknown[]) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag(...args);
}

/**
 * Identifies the user to GA4 so all subsequent hits are associated with
 * this ID. Call after any successful login or sign-up.
 */
function setGa4UserId(userId: string) {
  // GA4's built-in user_id field — enables cross-platform session stitching
  // (web, iOS, Android all set the same backend user ID here).
  gtag("set", { user_id: userId });
}

// ---------------------------------------------------------------------------
// Public hook
// ---------------------------------------------------------------------------

export function useAnalytics() {
  /**
   * Fire a GA4 `login` event.
   *
   * @param method  - auth method: 'email' | 'google' | 'apple' | 'facebook'
   * @param userId  - backend user id (for cross-platform session stitching)
   */
  const trackLogin = useCallback((method: string, userId?: string) => {
    if (userId) setGa4UserId(userId);
    gtag("event", "login", { method });
  }, []);

  /**
   * Fire a GA4 `sign_up` event.
   *
   * @param method  - auth method: 'email' | 'google' | 'apple' | 'facebook'
   * @param userId  - backend user id (for cross-platform session stitching)
   */
  const trackSignup = useCallback((method: string, userId?: string) => {
    if (userId) setGa4UserId(userId);
    gtag("event", "sign_up", { method });
  }, []);

  /**
   * Store a pending social OAuth return in sessionStorage so that when the
   * browser returns from the OAuth provider we can fire the correct event
   * (with the real user ID from SSR).
   */
  const markSocialAuthPending = useCallback((provider: string) => {
    if (typeof window === "undefined") return;
    const payload: SocialAuthPending = { provider };
    sessionStorage.setItem(SOCIAL_AUTH_PENDING_KEY, JSON.stringify(payload));
  }, []);

  /**
   * Read and clear the pending social auth flag from sessionStorage.
   * Returns the provider string if a social login was in progress, else null.
   */
  const consumeSocialAuthPending = useCallback((): SocialAuthPending | null => {
    if (typeof window === "undefined") return null;
    const raw = sessionStorage.getItem(SOCIAL_AUTH_PENDING_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(SOCIAL_AUTH_PENDING_KEY);
    try {
      return JSON.parse(raw) as SocialAuthPending;
    } catch {
      return null;
    }
  }, []);

  return {
    trackLogin,
    trackSignup,
    markSocialAuthPending,
    consumeSocialAuthPending,
  };
}
